// ClusterFormDrawer — create / edit a cluster (name + parent zone + default), the
// drawer container of the form-surface taxonomy (ADR-038). Mirrors ZoneFormDrawer,
// with the cluster divergences (ADR-052):
//
//  1. PARENT ZONE PICKER — the family's picker over the project's zones (`zonesSource`,
//     asked on the press — never for a closed drawer; a disabled zone takes no new
//     cluster, so it is never offered, while an edited cluster's current zone shows
//     marked), required on create, editable on edit. On create it rides
//     CreateClusterRequest; on edit a changed zone is a REPARENT (PATCH carries name +
//     zone_id).
//  2. REPARENT BLOCK (proactive, ADR-052 §5) — when the operator changes the zone on
//     an EXISTING cluster, we fire POST …/reparent/preview {zone_id}. If the move would
//     orphan any cluster-level chain (blocked), we show an inline error under the zone
//     field naming the orphaned chains and hold Save (the drawer's submitDisabled) —
//     "show, don't fail on click". A clean preview clears the block. The backend still guards
//     (the PATCH 409s CLUSTER_REPARENT_BLOCKED) — this is the friendly front edge.
//
// On create it lands on the new cluster's peek; on edit it applies only the changed
// fields (name/zone via PATCH, default via the dedicated set-default action — you
// don't un-default a cluster, you set another as default). The edit rides the row
// stamp (ADR-084): the shell seeds from the cluster as it is at each open, the
// PATCH carries the stamp and goes first (set-default is an act, which moves it),
// and a stale save shows the line with Load the latest.

import { useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Controller } from 'react-hook-form';
import { z } from 'zod';
import { DrawerForm } from '../../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../../components/patterns/forms/useFormShell.js';
import { Input } from '../../../components/ui/Input.js';
import { Label } from '../../../components/ui/Label.js';
import { Toggle } from '../../../components/ui/Toggle.js';
import { Alert } from '../../../components/ui/Alert.js';
import { Picker } from '../../../components/patterns/Picker.js';
import { zonesSource } from '../../../hooks/useZones.js';
import { clustersKeys, fetchCluster } from '../../../hooks/useClusters.js';
import {
  useCreateCluster,
  useReparentClusterPreview,
  useSetClusterDefault,
  useUpdateCluster,
} from '../../../hooks/useClusterMutations.js';
import type { BackingImpact } from '@ems-app/schemas/accountability/responses';

// Local form schema (ADR-038 §2): is_default is a required boolean here (the
// toggle's value), unlike the wire CreateClusterRequest where it defaults. name
// .max(32) matches clusters.name varchar(32). zone_id is the parent — 0 is the
// "nothing picked yet" sentinel (rejected by min(1)); a real zone id is positive.
const clusterFormSchema = z.object({
  name: z.string().trim().min(1).max(32),
  zone_id: z.number().int().min(1),
  is_default: z.boolean(),
});
type ClusterFormValues = z.infer<typeof clusterFormSchema>;

export interface ClusterFormTarget {
  id: number;
  name: string;
  zone_id: number;
  is_default: boolean;
  is_disabled: boolean;
  row_version: number;
}

export function ClusterFormDrawer({
  projectId,
  cluster,
  open,
  onClose,
  onCreated,
}: {
  projectId: string;
  /** Omit for create; provide the cluster for edit. */
  cluster?: ClusterFormTarget;
  open: boolean;
  onClose: () => void;
  /** Create only: called with the new cluster's id after a successful create — the list
   *  opens its peek (clusters are peek-only, ADR-051; there is no detail page to land on). */
  onCreated?: (id: number) => void;
}) {
  const { t } = useTranslation('clusters');
  const isEdit = Boolean(cluster);
  const zones = useMemo(() => zonesSource(projectId), [projectId]);
  const createCluster = useCreateCluster(projectId);
  const updateCluster = useUpdateCluster(projectId);
  const setDefault = useSetClusterDefault(projectId);
  const reparentPreview = useReparentClusterPreview(projectId);
  const createdId = useRef<number | null>(null);
  const qc = useQueryClient();

  // The proactive reparent block (ADR-052 §5): the blocked impact for the currently
  // selected zone on an existing cluster, or null when the move is clean / N/A.
  const [reparentBlock, setReparentBlock] = useState<BackingImpact | null>(null);

  const shell = useFormShell<ClusterFormValues, ClusterFormTarget>({
    schema: clusterFormSchema,
    row: cluster ?? null,
    values: (r) => ({ name: r?.name ?? '', zone_id: r?.zone_id ?? 0, is_default: r?.is_default ?? false }),
    reload: cluster ? () => readLatest(qc, clustersKeys.detail(projectId, cluster.id), () => fetchCluster(projectId, cluster.id, true)) : undefined,
    onSubmit: async (v, { stamp, changed }) => {
      if (cluster) {
        // Only what changed since the drawer opened. A changed name OR zone is a single PATCH (rename + reparent
        // share the route, which takes both) with the stamp, first.
        if ('name' in changed || 'zone_id' in changed) {
          await updateCluster.mutateAsync({ clusterId: cluster.id, body: { row_version: stamp, name: v.name, zone_id: v.zone_id } });
        }
        // Default is set, never unset — promoting a cluster clears the prior; you
        // don't "remove" a default (one always exists). Only act on false→true.
        if ('is_default' in changed && v.is_default) await setDefault.mutateAsync(cluster.id);
      } else {
        const r = await createCluster.mutateAsync({ name: v.name, zone_id: v.zone_id, is_default: v.is_default });
        createdId.current = r.id;
      }
    },
    onSuccess: () => {
      const newId = createdId.current;
      createdId.current = null;
      onClose();
      if (newId) onCreated?.(newId);
    },
  });

  // Clear the proactive reparent block whenever the drawer's open-state flips
  // (the React "adjust state on prop change" pattern, mirroring the manage drawer's
  // open-tracker) — the container re-seeds the fields on open; the block is this
  // drawer's own state, cleared beside it without an effect.
  const [openTracked, setOpenTracked] = useState(open);
  if (open !== openTracked) {
    setOpenTracked(open);
    setReparentBlock(null);
  }

  // When the zone changes on an EXISTING cluster, proactively preview the reparent.
  // Same zone (or create mode) → no block. A blocked result is surfaced inline +
  // holds Save; a clean one clears any prior block.
  const onZoneChange = (nextId: number, fieldOnChange: (v: number) => void) => {
    fieldOnChange(nextId);
    if (!cluster || nextId === cluster.zone_id || nextId < 1) {
      setReparentBlock(null);
      return;
    }
    reparentPreview.mutate(
      { clusterId: cluster.id, zoneId: nextId },
      { onSuccess: (impact) => setReparentBlock(impact.blocked ? impact : null) },
    );
  };

  return (
    <DrawerForm
      shell={shell}
      open={open}
      onClose={onClose}
      title={isEdit ? t('form.editTitle') : t('form.createTitle')}
      description={isEdit ? undefined : t('form.createHint')}
      submitLabel={isEdit ? t('form.save') : t('form.create')}
      // "Show, don't fail on click": a known-blocked reparent holds Save (the inline error under the zone field
      // explains it; the server re-guards).
      submitDisabled={reparentBlock?.blocked === true}
      autoFocusField="name"
    >
      <div>
        <Label htmlFor="cluster-name" required>
          {t('form.name')}
        </Label>
        <Input
          id="cluster-name"
          {...shell.form.register('name')}
          error={shell.form.formState.errors.name ? ' ' : undefined}
          placeholder={t('form.namePlaceholder')}
        />
      </div>
      <Controller
        control={shell.form.control}
        name="zone_id"
        render={({ field }) => (
          <div>
            <Picker
              source={zones}
              label={t('form.zone')}
              required
              value={field.value > 0 ? String(field.value) : null}
              onChange={(v) => onZoneChange(v ? Number(v) : 0, field.onChange)}
              placeholder={t('form.zonePlaceholder')}
              error={shell.form.formState.errors.zone_id ? ' ' : undefined}
            />
            {reparentBlock?.blocked && (
              <Alert variant="danger" className="mt-2">
                <p>{t('form.reparentBlocked')}</p>
                <ul className="mt-1 list-disc ps-5">
                  {reparentBlock.orphaned.map((o) => (
                    <li key={`${o.holder_id}:${o.responsibility_id}`}>
                      {t('form.reparentBlockedItem', { responsibility: o.responsibility_name, scope: o.scope_label })}
                    </li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        )}
      />
      {/* A switched-off cluster is never the default (the action door's rule too) — the choice is not offered. */}
      {!cluster?.is_disabled && (
        <Controller
          control={shell.form.control}
          name="is_default"
          render={({ field }) => (
            <Toggle checked={field.value} onChange={field.onChange} label={t('form.setDefault')} />
          )}
        />
      )}
    </DrawerForm>
  );
}
